import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "@/common/providers/i18n-provider";
import {
  CSV_SPLIT_ACCOUNT_ISSUE_CODES,
  MAX_CSV_SPLIT_ALLOCATIONS,
  MIN_CSV_SPLIT_ALLOCATIONS,
  csvSplitBalance,
  type CsvRowSplit,
  type CsvSplitAllocation,
  type CsvSplitDiagnostic,
  type CsvSplitIssue,
} from "@/lib/csv-split-allocations";
import {
  preparedAmountText,
  preparedRowSplitKey,
  type PreparedCsvRow,
} from "@/lib/csv-to-beancount";
import type { CsvSplitAction } from "@/lib/tools-analytics";
import { isolateLtr } from "./conversion-note-i18n";

/**
 * Most rows offered in the "row to split" picker. A split is a deliberate
 * per-receipt edit, not a bulk operation, so the picker stays bounded on a
 * full-year export and says how many rows it leaves out.
 */
const MAX_SPLIT_ROW_CHOICES = 200;

/** Most rows that can carry a split at once — keeps the editor DOM bounded. */
const MAX_CSV_SPLIT_ROWS = 20;

/** One editable allocation; `id` keeps React keys and focus stable across removals. */
export interface CsvSplitAllocationDraft extends CsvSplitAllocation {
  id: string;
}

/**
 * A reader's in-memory split for one row. Structurally a {@link CsvRowSplit},
 * so the widget hands the list to `emitBeancount` as `rowSplits` unchanged:
 * blank allocations keep their position, which is what lets a diagnostic's
 * `index` point back at the exact field shown here.
 */
export interface CsvSplitDraft extends CsvRowSplit {
  allocations: CsvSplitAllocationDraft[];
}

/**
 * The explicit review clicks a draft change can carry. Allocation edits,
 * add/remove-line clicks and keystrokes pass none, so the widget's telemetry
 * hook can never count typing or automatic recalculation.
 */
export type CsvSplitReviewAction = Extract<CsvSplitAction, "start" | "reset" | "remove">;

type Translate = (key: string, substitutions?: Record<string, string>) => string;

interface CsvSplitReviewProps {
  /** Every convertible row of the current source, excluded ones included. */
  rows: readonly PreparedCsvRow[];
  drafts: readonly CsvSplitDraft[];
  onDraftsChange: (next: CsvSplitDraft[], action?: CsvSplitReviewAction) => void;
  /** `ConvertResult.splitDiagnostics` — selected rows only. */
  diagnostics: readonly CsvSplitDiagnostic[] | undefined;
  excludedRowIndexes: ReadonlySet<number>;
  counterAccount: string;
  /** Deferred input still catching up: no diagnostics, no editing. */
  pending: boolean;
}

function rowDescription(t: Translate, row: PreparedCsvRow): string {
  return row.description === "" ? t("toolsCsvRules.blankDescription") : row.description;
}

function absoluteAmountText(row: PreparedCsvRow): string {
  return preparedAmountText(row).replace(/^-/, "");
}

/** Localized text for one typed split issue (never the model's English reason). */
function issueMessage(t: Translate, issue: CsvSplitIssue, locale: string): string {
  const n = (index: number) => (index + 1).toLocaleString(locale);
  switch (issue.code) {
    case "unknown_row":
      return t("toolsCsvSplit.issues.unknownRow");
    case "row_unavailable":
      return t("toolsCsvSplit.issues.rowUnavailable");
    case "stale_row":
      return t("toolsCsvSplit.issues.staleRow");
    case "too_few_allocations":
      return t("toolsCsvSplit.issues.tooFew", {
        min: issue.min.toLocaleString(locale),
      });
    case "too_many_allocations":
      return t("toolsCsvSplit.issues.tooMany", {
        max: issue.max.toLocaleString(locale),
      });
    case "missing_account":
      return t("toolsCsvSplit.issues.missingAccount", { n: n(issue.index) });
    case "missing_amount":
      return t("toolsCsvSplit.issues.missingAmount", { n: n(issue.index) });
    case "invalid_account":
      return t("toolsCsvSplit.issues.invalidAccount", { n: n(issue.index) });
    case "source_account":
      return t("toolsCsvSplit.issues.sourceAccount", { n: n(issue.index) });
    case "invalid_amount":
      return t("toolsCsvSplit.issues.invalidAmount", { n: n(issue.index) });
    case "nonpositive_amount":
      return t("toolsCsvSplit.issues.nonpositiveAmount", {
        n: n(issue.index),
      });
    case "unsupported_precision":
      return t("toolsCsvSplit.issues.precision", {
        n: n(issue.index),
        max: issue.maxDecimals.toLocaleString(locale),
      });
    case "sum_mismatch":
      return t("toolsCsvSplit.issues.sumMismatch", {
        allocated: issue.allocatedText,
        expected: issue.expectedText,
      });
  }
}

function issueIndex(issue: CsvSplitIssue): number | undefined {
  return "index" in issue ? issue.index : undefined;
}

/**
 * Per-row split editor: pick one row, then divide it between two or more
 * destination accounts with a live remaining amount. Every control is a
 * native select, input or button, so it is keyboard-operable with no extra
 * wiring; focus follows the reader's action when a row or allocation
 * appears or disappears.
 *
 * It is deliberately separate from category assignments and description
 * rules (reusable, saved in profiles) and from overlap exclusions (which
 * decide *whether* a row is emitted): a split is a one-off edit to one row of
 * this file, held in the widget's memory only. The widget owns the drafts
 * and ties them to the current source context; this component only edits
 * them and renders the emitter's verdict.
 */
export function CsvSplitReview({
  rows,
  drafts,
  onDraftsChange,
  diagnostics,
  excludedRowIndexes,
  counterAccount,
  pending,
}: CsvSplitReviewProps) {
  const { t, locale } = useTranslation();
  const baseId = useId();
  const idCounter = useRef(0);
  const pendingFocus = useRef<string | null>(null);
  const [pickedRow, setPickedRow] = useState("");

  useEffect(() => {
    if (pendingFocus.current === null) return;
    const target = document.getElementById(pendingFocus.current);
    pendingFocus.current = null;
    target?.focus();
  });

  const pickerId = `${baseId}-row`;
  const allocationFieldId = (
    rowIndex: number,
    allocationId: string,
    field: "account" | "amount" | "error" | "label" = "account",
  ) => `${baseId}-r${rowIndex}-${allocationId}-${field}`;
  const addAllocationId = (rowIndex: number) => `${baseId}-r${rowIndex}-add`;

  const rowByIndex = new Map(rows.map((row) => [row.rowIndex, row]));
  const splitRowIndexes = new Set(drafts.map((draft) => draft.rowIndex));
  const available = rows.filter((row) => !splitRowIndexes.has(row.rowIndex));
  const choices = available.slice(0, MAX_SPLIT_ROW_CHOICES);
  const hiddenChoices = available.length - choices.length;
  const picked =
    choices.find((row) => String(row.rowIndex) === pickedRow) ?? choices[0];
  const atMax = drafts.length >= MAX_CSV_SPLIT_ROWS;
  const issuesByRow = new Map(
    (pending ? [] : (diagnostics ?? [])).map((d) => [d.rowIndex, d.issues]),
  );

  function newAllocation(account = "", amountText = ""): CsvSplitAllocationDraft {
    idCounter.current += 1;
    return { id: `a${idCounter.current}`, account, amountText };
  }

  /** A fresh split: the row's current account carries the full amount, plus one empty line to divide into. */
  function initialAllocations(row: PreparedCsvRow): CsvSplitAllocationDraft[] {
    return [
      newAllocation(row.counterAccount, absoluteAmountText(row)),
      newAllocation(),
    ];
  }

  function updateDraft(
    rowIndex: number,
    update: (draft: CsvSplitDraft) => CsvSplitDraft,
    action?: CsvSplitReviewAction,
  ) {
    let changed = false;
    const next = drafts.map((draft) => {
      if (draft.rowIndex !== rowIndex) return draft;
      const updated = update(draft);
      if (updated !== draft) changed = true;
      return updated;
    });
    if (changed) onDraftsChange(next, action);
  }

  function startSplit() {
    if (picked === undefined || atMax) return;
    const allocations = initialAllocations(picked);
    pendingFocus.current = allocationFieldId(picked.rowIndex, allocations[0]!.id);
    onDraftsChange(
      [
        ...drafts,
        {
          rowIndex: picked.rowIndex,
          sourceKey: preparedRowSplitKey(picked),
          allocations,
        },
      ].sort((a, b) => a.rowIndex - b.rowIndex),
      "start",
    );
  }

  function removeSplit(rowIndex: number) {
    pendingFocus.current = pickerId;
    setPickedRow(String(rowIndex));
    onDraftsChange(
      drafts.filter((draft) => draft.rowIndex !== rowIndex),
      "remove",
    );
  }

  function resetSplit(row: PreparedCsvRow) {
    const allocations = initialAllocations(row);
    pendingFocus.current = allocationFieldId(row.rowIndex, allocations[0]!.id);
    updateDraft(row.rowIndex, (draft) => ({
      ...draft,
      sourceKey: preparedRowSplitKey(row),
      allocations,
    }), "reset");
  }

  function addAllocation(rowIndex: number) {
    const allocation = newAllocation();
    pendingFocus.current = allocationFieldId(rowIndex, allocation.id);
    updateDraft(rowIndex, (draft) =>
      draft.allocations.length >= MAX_CSV_SPLIT_ALLOCATIONS
        ? draft
        : { ...draft, allocations: [...draft.allocations, allocation] },
    );
  }

  function removeAllocation(rowIndex: number, allocationId: string) {
    const draft = drafts.find((d) => d.rowIndex === rowIndex);
    if (draft === undefined) return;
    const at = draft.allocations.findIndex((a) => a.id === allocationId);
    const next = draft.allocations.filter((a) => a.id !== allocationId);
    const neighbour = next[at] ?? next[at - 1];
    pendingFocus.current =
      neighbour !== undefined
        ? allocationFieldId(rowIndex, neighbour.id)
        : addAllocationId(rowIndex);
    updateDraft(rowIndex, (d) => ({ ...d, allocations: next }));
  }

  function editAllocation(
    rowIndex: number,
    allocationId: string,
    patch: Partial<CsvSplitAllocation>,
  ) {
    updateDraft(rowIndex, (draft) => ({
      ...draft,
      allocations: draft.allocations.map((a) =>
        a.id === allocationId ? { ...a, ...patch } : a,
      ),
    }));
  }

  const rowSummary = (row: PreparedCsvRow) =>
    t("toolsCsvSplit.rowOption", {
      line: row.line.toLocaleString(locale),
      date: row.date,
      description: rowDescription(t, row),
      // A native <option> cannot hold a `dir="ltr"` span, so the signed
      // amount is wrapped in Unicode LTR isolates: under an RTL select the
      // bidi algorithm would otherwise move a refund's or charge's leading
      // minus to the far side of the number ("12.50- USD").
      amount: isolateLtr(`${preparedAmountText(row)} ${row.currency}`),
    });

  return (
    <section className="tool-overlap" aria-labelledby={`${baseId}-heading`}>
      <h2 id={`${baseId}-heading`} className="tool-settings-heading">
        {t("toolsCsvSplit.title")}
      </h2>
      <p className="tool-local-note">{t("toolsCsvSplit.description")}</p>

      {pending ? (
        <p className="tool-status" role="status">
          {t("tools.csvWidget.status.pending")}
        </p>
      ) : (
        <>
          {choices.length > 0 && !atMax ? (
            <div className="tool-settings-grid">
              <label className="tool-control-label" htmlFor={pickerId}>
                <span className="tool-label-text">
                  {t("toolsCsvSplit.rowLabel")}
                </span>
                <select
                  id={pickerId}
                  value={picked === undefined ? "" : String(picked.rowIndex)}
                  onChange={(event) => setPickedRow(event.target.value)}
                  className="tool-control"
                >
                  {choices.map((row) => (
                    <option key={row.rowIndex} value={row.rowIndex}>
                      {rowSummary(row)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="tool-file-actions">
                <button type="button" onClick={startSplit}>
                  {t("toolsCsvSplit.start")}
                </button>
              </div>
            </div>
          ) : null}
          {hiddenChoices > 0 ? (
            <p className="tool-local-note">
              {t("toolsCsvSplit.moreRows", {
                count: hiddenChoices.toLocaleString(locale),
              })}
            </p>
          ) : null}
          {atMax ? (
            <p className="tool-local-note">
              {t("toolsCsvSplit.maxed", {
                max: MAX_CSV_SPLIT_ROWS.toLocaleString(locale),
              })}
            </p>
          ) : null}

          {drafts.map((draft) => {
            const row = rowByIndex.get(draft.rowIndex);
            const headingId = `${baseId}-r${draft.rowIndex}-heading`;
            if (row === undefined) {
              // Unreachable while the widget ties drafts to the source
              // context, but a split must never render as if it applied.
              const message = t("toolsCsvSplit.issues.rowUnavailable");
              return (
                <div key={draft.rowIndex} role="group" aria-label={message}>
                  <p className="tool-notice">{message}</p>
                  <div className="tool-file-actions">
                    <button type="button" onClick={() => removeSplit(draft.rowIndex)}>
                      {t("toolsCsvSplit.remove")}
                    </button>
                  </div>
                </div>
              );
            }
            const excluded = excludedRowIndexes.has(row.rowIndex);
            const issues = excluded ? [] : (issuesByRow.get(row.rowIndex) ?? []);
            const rowIssues = issues.filter((i) => issueIndex(i) === undefined);
            const balance = csvSplitBalance(draft.allocations, preparedAmountText(row));
            return (
              <div
                key={row.rowIndex}
                role="group"
                aria-labelledby={headingId}
                className="tool-notes"
              >
                <h3 id={headingId} className="tool-notes-heading">
                  {t("toolsCsvSplit.rowHeading", {
                    line: row.line.toLocaleString(locale),
                  })}
                </h3>
                <p className="tool-local-note">
                  <span dir="ltr">{row.date}</span>
                  {" · "}
                  <span dir="auto">{rowDescription(t, row)}</span>
                  {" · "}
                  <span dir="ltr">
                    {preparedAmountText(row)} {row.currency}
                  </span>
                </p>
                {excluded ? (
                  <p className="tool-local-note">
                    {t("toolsCsvSplit.excluded")}
                  </p>
                ) : null}
                <ol className="tool-split-allocations">
                  {draft.allocations.map((allocation, index) => {
                    const n = (index + 1).toLocaleString(locale);
                    const label = t("toolsCsvSplit.allocation", { n });
                    const fieldIssues = issues.filter((i) => issueIndex(i) === index);
                    const accountIssue = fieldIssues.find((i) =>
                      CSV_SPLIT_ACCOUNT_ISSUE_CODES.has(i.code),
                    );
                    const amountIssue = fieldIssues.find((i) => i !== accountIssue);
                    const accountId = allocationFieldId(row.rowIndex, allocation.id);
                    const amountId = allocationFieldId(row.rowIndex, allocation.id, "amount");
                    const errorId = allocationFieldId(row.rowIndex, allocation.id, "error");
                    const labelId = allocationFieldId(row.rowIndex, allocation.id, "label");
                    return (
                      <li key={allocation.id}>
                        <div role="group" aria-labelledby={labelId}>
                          <p id={labelId} className="tool-label-text">
                            {label}
                          </p>
                          <div className="tool-settings-grid">
                            <label className="tool-control-label" htmlFor={accountId}>
                              <span className="tool-label-text">
                                {t("toolsCsvSplit.account")}
                              </span>
                              <input
                                id={accountId}
                                value={allocation.account}
                                onChange={(event) =>
                                  editAllocation(row.rowIndex, allocation.id, {
                                    account: event.target.value,
                                  })
                                }
                                placeholder={counterAccount}
                                spellCheck={false}
                                dir="ltr"
                                aria-invalid={accountIssue !== undefined}
                                aria-describedby={
                                  accountIssue !== undefined ? errorId : undefined
                                }
                                className="tool-control tool-account"
                              />
                            </label>
                            <label className="tool-control-label" htmlFor={amountId}>
                              <span className="tool-label-text">
                                {t("toolsCsvSplit.amount", {
                                  currency: row.currency,
                                })}
                              </span>
                              <input
                                id={amountId}
                                value={allocation.amountText}
                                onChange={(event) =>
                                  editAllocation(row.rowIndex, allocation.id, {
                                    amountText: event.target.value,
                                  })
                                }
                                inputMode="decimal"
                                spellCheck={false}
                                dir="ltr"
                                aria-invalid={amountIssue !== undefined}
                                aria-describedby={
                                  amountIssue !== undefined ? errorId : undefined
                                }
                                className="tool-control tool-account"
                              />
                            </label>
                          </div>
                          {fieldIssues.length > 0 ? (
                            <p id={errorId} className="tool-notice">
                              {fieldIssues
                                .map((i) => issueMessage(t, i, locale))
                                .join(" ")}
                            </p>
                          ) : null}
                          <div className="tool-file-actions">
                            <button
                              type="button"
                              onClick={() =>
                                removeAllocation(row.rowIndex, allocation.id)
                              }
                              disabled={draft.allocations.length <= MIN_CSV_SPLIT_ALLOCATIONS}
                              aria-label={t(
                                "toolsCsvSplit.removeAllocationLabel",
                                { n },
                              )}
                            >
                              {t("toolsCsvSplit.removeAllocation")}
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                {balance !== null ? (
                  <p className="tool-status" role="status">
                    <span dir="auto">
                      {balance.sign === 0
                        ? t("toolsCsvSplit.balanced", {
                            amount: balance.expectedText,
                            currency: row.currency,
                          })
                        : balance.sign > 0
                          ? t("toolsCsvSplit.remaining", {
                              amount: balance.remainingText,
                              currency: row.currency,
                            })
                          : t("toolsCsvSplit.over", {
                              amount: balance.remainingText.replace(/^-/, ""),
                              currency: row.currency,
                            })}
                    </span>
                  </p>
                ) : null}
                {rowIssues.length > 0 ? (
                  <ul className="tool-notes-list">
                    {rowIssues.map((issue) => (
                      <li key={issue.code}>{issueMessage(t, issue, locale)}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="tool-file-actions">
                  <button
                    id={addAllocationId(row.rowIndex)}
                    type="button"
                    onClick={() => addAllocation(row.rowIndex)}
                    disabled={draft.allocations.length >= MAX_CSV_SPLIT_ALLOCATIONS}
                  >
                    {t("toolsCsvSplit.addAllocation")}
                  </button>
                  <button type="button" onClick={() => resetSplit(row)}>
                    {t("toolsCsvSplit.reset")}
                  </button>
                  <button type="button" onClick={() => removeSplit(row.rowIndex)}>
                    {t("toolsCsvSplit.remove")}
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}
